import { forwardRef, type PropsWithChildren } from 'react';

import { HistogramChartRoot } from './HistogramChartRoot.js';
import { Annotations } from './slots/annotations/Annotations.js';
import { Marker } from './slots/annotations/Marker.js';
import { AnnotationsTooltip } from './slots/annotations/Tooltip.js';
import { Track } from './slots/annotations/Track.js';
import { HistogramChartDownloadCSVSlot } from './slots/DownloadCSV.js';
import { Legend } from './slots/Legend.js';
import { Pan } from './slots/Pan.js';
import { Select } from './slots/Select.js';
import { ThresholdIndicator } from './slots/ThresholdIndicator.js';
import { Toolbar } from './slots/Toolbar.js';
import { Tooltip } from './slots/Tooltip.js';
import { XAxis } from './slots/XAxis.js';
import { YAxis } from './slots/YAxis.js';
import { Zoom } from './slots/Zoom.js';
import type { HistogramChartProps } from './types/histogram-props.js';
import type { HistogramChartRef } from './types/histogram-ref.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import { HistogramToXY } from '../xy-chart-adoption/histogram/HistogramToXY.js';
import type { _XYChartLegacyProps } from '../xy-chart-adoption/shared/types/legacy-props.js';

export const HistogramChartSwitcher = forwardRef<
  HistogramChartRef,
  PropsWithChildren<HistogramChartProps & _XYChartLegacyProps>
>((props, forwardedRef) => {
  const { __useLegacy, ...rest } = props;
  if (__useLegacy) {
    return <HistogramChartRoot {...rest} ref={forwardedRef} />;
  }
  return <HistogramToXY {...rest} ref={forwardedRef} />;
});

(
  HistogramChartSwitcher as typeof HistogramChartSwitcher & {
    displayName: string;
  }
).displayName = 'HistogramChart';
/**
 * A `HistogramChart` is a type of graph that represents the distribution of numerical data.
 * It’s constructed by dividing the entire range of values into a series of intervals—known as bins
 * and then counting how many values fall into each interval.
 * The bins are typically of equal size and are adjacent to each other, with no gaps. Each bin is represented by a bar,
 * where the height of the bar reflects the frequency or count of data points within that interval
 * @public
 */
export const HistogramChart = Object.assign(HistogramChartSwitcher, {
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  Annotations,
  ColorRule,
  ThresholdIndicator,
  Select,
  Intent,
  Toolbar,
  DownloadCSV: HistogramChartDownloadCSVSlot,
  Pan,
  Zoom,
});

/**
 * Annotations Slots
 * @public
 */
export const HistogramAnnotations = Object.assign(
  {},
  {
    Marker,
    Track,
    Tooltip: AnnotationsTooltip,
  },
);
